import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const inputCls =
  "w-full bg-[#F7F7F2] border border-[#E4E4DE] px-4 py-3 text-sm text-zinc-900 placeholder-zinc-600 focus:border-[#C8FF1E] focus:outline-none transition-colors";

const FAQS = [
  ["When will my order ship?", "Every order leaves our Antwerp studio within 48 hours. You'll get a tracking link by email the moment it ships."],
  ["How do the sizes run?", "Our tees are cut boxy and oversized by design. Take your usual size for the intended fit, or size down for a regular silhouette."],
  ["Do you restock sold-out drops?", "Almost never. Each drop is a numbered run — once it's gone, it moves to the archive permanently."],
  ["Can I return or exchange?", "Yes — 30 days on unworn garments. Exchanges for a different size ship free."],
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "Order Query", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const valid = form.name.trim() && form.email.includes("@") && form.message.trim().length > 3;

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received — we reply within 24h");
      setForm({ name: "", email: "", subject: "Order Query", message: "" });
    } catch {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-16">
      <Seo
        title="Contact the Studio — Marabou Design"
        description="Questions about an order, wholesale or a custom print? Contact the Marabou Design studio — we reply within 24 hours."
      />
      <section className="border-b border-[#E4E4DE] py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#4D7C0F]/90">Contact the studio</p>
          <motion.h1
            className="mt-4 font-display text-4xl sm:text-6xl font-extrabold uppercase tracking-tighter leading-[0.9]"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            Talk to a human
          </motion.h1>
          <p className="mt-4 font-mono text-xs uppercase tracking-widest text-zinc-500">
            Average response time: under 24 hours
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14 grid gap-14 lg:grid-cols-2">
        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          className="space-y-4"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <input data-testid="contact-name-input" className={inputCls} placeholder="Your name" value={form.name} onChange={set("name")} />
          <input data-testid="contact-email-input" className={inputCls} type="email" placeholder="Email address" value={form.email} onChange={set("email")} />
          <select data-testid="contact-subject-select" className={inputCls} value={form.subject} onChange={set("subject")}>
            {["Order Query", "Wholesale", "Custom Print", "PR / Press"].map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
          <textarea
            data-testid="contact-message-input"
            className={`${inputCls} min-h-36 resize-y`}
            placeholder="How can we help?"
            value={form.message}
            onChange={set("message")}
          />
          <button
            data-testid="contact-submit-btn"
            disabled={!valid || sending}
            className="w-full bg-[#C8FF1E] py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black hover:bg-[#B0E600] disabled:opacity-40 transition-colors"
          >
            {sending ? "Sending…" : "Send message"}
          </button>
        </motion.form>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="border border-[#E4E4DE] p-6">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#4D7C0F]">Studio</p>
            <p className="mt-4 text-sm text-zinc-600 leading-relaxed">
              Unit 7, Concrete Works<br />Antwerp, BE 2000<br />studio@maraboudesign.com
            </p>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-zinc-600">
              Mon–Fri / 09:00–18:00 CET — showroom by appointment
            </p>
          </div>
          <div className="mt-8">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">Frequently asked</p>
            <Accordion type="single" collapsible className="mt-4 border-t border-[#E4E4DE]">
              {FAQS.map(([q, a], i) => (
                <AccordionItem key={q} value={`faq-${i}`} className="border-[#E4E4DE]">
                  <AccordionTrigger data-testid={`faq-${i}`} className="text-left font-mono text-xs uppercase tracking-widest text-zinc-600 hover:text-[#4D7C0F]">
                    {q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-zinc-500 leading-relaxed">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </motion.div>
      </section>
    </main>
  );
}
